function filter(nodelist, fn) {
  const filtered = [];

  for (let i = 0; i < nodelist.length; i++) {
    const node = nodelist[i];

    if (fn(node)) {
      filtered.push(node);
    }
  }

  return filtered;
}

export default class VariantHelper {
  constructor({
    addToCartButton = null, // Add to cart button element
    priceFields = null, // Price field elements
    productForm = null, // Product form element
    productGallery = null, // Product gallery object
    selector = null, // Variant select or input elements,
    type = 'select', // Type of selector element
    productJSON = null, // Product JSON
    productSettings = null, // Product settings JSON
    unitPrice = null, // Unit price element
    taxLine = null, // Tax line element
    quantityInput = null, // Quantity input element
  }) {
    this.callbacks = [];

    this.options = {
      addToCartButton,
      priceFields,
      productForm,
      productGallery,
      selector,
      type,
      productJSON,
      productSettings,
      unitPrice,
      taxLine,
      quantityInput,
    };

    this.enableHistory = false;
    this.masterSelect = this.options.productForm.querySelector('.product-select');

    // Disabled history state updating in the TE
    const isShopify = window.Shopify && window.Shopify.preview_host;

    if (window.history
      && window.history.replaceState
      && this.options.productSettings.enableHistory
      && !isShopify) {
      this.enableHistory = true;
    }

    this._init();
    this._bindEvents();
  }

  onVariantChange(callback) {
    if (this.callbacks.indexOf(callback) >= 0) {
      return;
    }

    this.callbacks.push(callback);
  }

  offVariantChange(callback) {
    if (this.callbacks.indexOf(callback) === -1) {
      return;
    }

    this.callbacks.splice(this.callbacks.indexOf(callback));
  }

  _init() {
    if (this.options.type === 'select') {
      this.options.selector.forEach(selector => this._setSelectLabel(selector));
    }
  }

  _bindEvents() {
    this.options.selector.forEach(selector => selector.addEventListener('change', event => this._variantChange(event)));
  }

  unload() {
    this.options.selector.forEach(selector => selector.removeEventListener('change', event => this._variantChange(event)));
  }

  _setSelectLabel(select) {
    if (!select) return;

    const { value, previousSibling } = select;

    previousSibling.innerText = value;
  }

  getCurrentOptions() {
    const productOptions = [];
    let inputs = this.options.selector;

    if (this.options.type === 'radio') {
      inputs = filter(inputs, input => input.checked);
    }

    inputs.forEach(input => productOptions.push(input.value));

    return productOptions;
  }

  getVariantFromOptions(productOptions) {
    if (this.options.productJSON.variants == null) { return; }

    let foundVariant = null;

    for (let i = 0; i < this.options.productJSON.variants.length; i++) {
      const variant = this.options.productJSON.variants[i];

      const isMatch = productOptions.every((value, index) => variant.options[index] === value);

      if (isMatch) {
        foundVariant = variant;
      }
    }

    return foundVariant;
  }

  _updateMasterSelect(variant) {
    if (variant == null) { return; }

    const event = document.createEvent('Event');

    event.initEvent('change', true, true);
    this.masterSelect.value = variant.id;
    this.masterSelect.dispatchEvent(event);
  }

  _updatePrice(variant) {
    const {
      addToCartButton,
      priceFields,
      productSettings,
      unitPrice,
      taxLine,
      productDetails,
    } = this.options;

    const unitPriceAmount = unitPrice ? unitPrice.querySelector('[data-unit-price-amount]') : null;
    const unitPriceMeasure = unitPrice ? unitPrice.querySelector('[data-unit-price-measure]') : null;
    const unitPriceQuantity = unitPrice ? unitPrice.querySelector('[data-unit-price-quantity]') : null;

    if (variant) {
      // strip all of the old data attributes

      priceFields.forEach(priceField => {
        const moneyEls = priceField.querySelectorAll('.money');

        moneyEls.forEach(moneyEl => {
          for (let j = 0; j < moneyEl.attributes.length; j++) {
            const attribute = moneyEl.attributes[j];
            if (attribute.name.indexOf('data-') > -1) {
              moneyEl.removeAttribute(attribute.name);
            }
          }
        });
      });

      if (taxLine) {
        if (variant.taxable) {
          taxLine.classList.remove('hidden');
        } else {
          taxLine.classList.add('hidden');
        }
      }

      if (variant.available && !document.body.classList.contains('template-password')) {
        addToCartButton.value = productSettings.addToCartText;
        addToCartButton.classList.remove('disabled');
        addToCartButton.removeAttribute('disabled');
      } else {
        addToCartButton.value = productSettings.soldOutText;
        addToCartButton.classList.add('disabled');
        addToCartButton.setAttribute('disabled', '');
      }

      if (variant.compare_at_price > variant.price) {
        priceFields.forEach(priceField => {
          const moneyEls = priceField.querySelectorAll('.money:not(.original)');

          moneyEls.forEach(moneyEl => {
            moneyEl.innerHTML = window.Theme.Currency.formatMoney(variant.price, window.Theme.moneyFormat);
          });
        });
        priceFields.forEach(priceField => {
          const moneyEls = priceField.querySelectorAll('.money.original');

          moneyEls.forEach(moneyEl => {
            moneyEl.innerHTML = window.Theme.Currency.formatMoney(variant.compare_at_price, window.Theme.moneyFormat);
            moneyEl.classList.add('visible');
          });
        });
      } else {
        priceFields.forEach(priceField => {
          const moneyEls = priceField.querySelectorAll('.money');

          moneyEls.forEach(moneyEl => {
            moneyEl.innerHTML = window.Theme.Currency.formatMoney(variant.price, window.Theme.moneyFormat);
          });
        });
        priceFields.forEach(priceField => {
          const moneyEls = priceField.querySelectorAll('.money.original');

          moneyEls.forEach(moneyEl => {
            moneyEl.classList.remove('visible');
          });
        });
      }

      if (variant.unit_price) {
        unitPrice.classList.remove('hidden');
        if (unitPriceQuantity) {
          unitPriceQuantity.innerHTML = `${variant.unit_price_measurement.quantity_value}${variant.unit_price_measurement.quantity_unit}`;
        }

        if (unitPriceAmount) {
          unitPriceAmount.innerHTML = window.Theme.Currency.formatMoney(variant.unit_price, Theme.moneyFormat);
        }

        if (unitPriceMeasure) {
          if (variant.unit_price_measurement.reference_value !== 1) {
            unitPriceMeasure.innerHTML = `${variant.unit_price_measurement.reference_value}${variant.unit_price_measurement.reference_unit}`;
          } else {
            unitPriceMeasure.innerHTML = variant.unit_price_measurement.reference_unit;
          }
        }
      } else if (unitPrice) {
        unitPrice.classList.add('hidden');
      }
    } else {
      // Variant doesn't exist
      addToCartButton.value = productSettings.unavailableText;
      addToCartButton.classList.add('disabled');
      addToCartButton.setAttribute('disabled', '');
    }
  }

  _updateImages(variant) {
    const imageId = __guard__(variant != null ? variant.featured_image : undefined, x => x.id);
    const imagePosition = __guard__(variant != null ? variant.featured_image : undefined, x1 => x1.position) - 1;

    this.options.productGallery.selectMediaByVariant(variant);
  }

  _updateHistory(variant) {
    if (!this.enableHistory || (variant == null)) { return; }

    const re = new RegExp('([?|&]variant=)[0-9]+(&{0,1})');

    let { search } = window.location;

    if (search.match(re)) {
      search = search.replace(re, `$1${variant.id}$2`);
    } else if (search.indexOf('?') !== -1) {
      search += `&variant=${variant.id}`;
    } else {
      search += `?variant=${variant.id}`;
    }

    const newUrl = [
      window.location.protocol,
      '//',
      window.location.host,
      window.location.pathname,
      search,
    ];

    const variantUrl = newUrl.join('');
    window.history.replaceState({ path: variantUrl }, '', variantUrl);
  }

  _variantChange(event) {
    if (this.options.type === 'select') {
      this._setSelectLabel(event.currentTarget);
    }

    const productOptions = this.getCurrentOptions();
    const variant = this.getVariantFromOptions(productOptions);

    this._updateMasterSelect(variant);
    this._updatePrice(variant);
    this._updateImages(variant);
    this._updateHistory(variant);
    this.callbacks.forEach(callback => callback(variant));
  }
}

function __guard__(value, transform) {
  return (typeof value !== 'undefined' && value !== null) ? transform(value) : undefined;
}
